import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router';
import UnsavedChangesDialog from '../../components/muiLegacy/Dialogs/CancelDialog/UnsavedChangesDialog';
import { useCancelDialog } from '../../components/muiLegacy/Dialogs/CancelDialog/useCancelDialog';
import VoxelPageContainer from '../../components/voxel/Containers/VoxelPageContainer';
import VoxelMenu, { MenuItem } from '../../components/voxel/Menu/VoxelMenu';
import VoxelText from '../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { CurrentUserGq } from '../../graphql/users/models/CurrentUserGq';
import { FrontendUserEventGq } from '../../graphql/users/queries/useFrontendLogUserEventGq';
import { usersApiService } from '../../graphql/users/usersApiService';
import ProjectAppBar from '../ProjectPage/ProjectAppBar/ProjectAppBar';
import { useProjectPageTitle } from '../ProjectPage/useProjectPageTitle';
import DataSettings from './DataSettingsPage/DataSettingsPage';
import TeamAccessPage from './TeamAccessPage/TeamAccessPage';
import GeneralSettingsPage from './GeneralSettingsPage/GeneralSettingsPage';

export interface ProjectSettingsPageProps {
  project: ProjectGq;
  reload: () => Promise<any>;
  user: CurrentUserGq;
  path: string;
}

export enum ProjectSettingsMenuChoices {
  General = 'general',
  TeamAccess = 'teamAccess',
  DataCollectionSettings = 'dataCollection',
}

const ProjectSettingsPage: React.FC<ProjectSettingsPageProps> = ({
  project,
  reload,
  user,
  path,
}) => {
  // Log user event
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
  useEffect(() => {
    logUserEventMutation({
      event: FrontendUserEventGq.VISIT_PROJECT_SETTINGS_PAGE,
      projectId: project.id,
    });
  }, [project.id]);

  // Navigation
  const menuItems: MenuItem[] = [
    {
      name: 'General',
      id: ProjectSettingsMenuChoices.General,
    },
    {
      name: 'Data settings',
      id: ProjectSettingsMenuChoices.DataCollectionSettings,
    },
    {
      name: 'Team access',
      id: ProjectSettingsMenuChoices.TeamAccess,
    },
  ];

  // Reload project upon open page
  React.useEffect(() => {
    reload();
  }, []);

  useProjectPageTitle('Settings', project);

  // Combined cancel dialog bag
  const [combo, setCombo] = useState<{
    generalNameDirty?: boolean;
    generalNameSaveFn?: () => Promise<unknown>;
    generalDomainDirty?: boolean;
    generalDomainSaveFn?: () => Promise<unknown>;
    generalCurrencyDirty?: boolean;
    generalCurrencySaveFn?: () => Promise<unknown>;
    languageDirty?: boolean;
    languageSaveFn?: () => Promise<unknown>;
    keywordNegativeTermsDirty?: boolean;
    keywordNegativeTermsSaveFn?: () => Promise<unknown>;
    locationDirty?: boolean;
    locationSaveFn?: () => Promise<unknown>;
  }>({});

  const dirty =
    !!combo.generalNameDirty ||
    !!combo.generalDomainDirty ||
    !!combo.generalCurrencyDirty ||
    !!combo.languageDirty ||
    !!combo.keywordNegativeTermsDirty ||
    !!combo.locationDirty;

  const combinedSave = useCallback(() => {
    return Promise.resolve()
      .then(() =>
        combo.generalNameDirty && combo.generalNameSaveFn
          ? combo.generalNameSaveFn()
          : Promise.resolve()
      )
      .then(() =>
        combo.generalDomainDirty && combo.generalDomainSaveFn
          ? combo.generalDomainSaveFn()
          : Promise.resolve()
      )
      .then(() =>
        combo.generalCurrencyDirty && combo.generalCurrencySaveFn
          ? combo.generalCurrencySaveFn()
          : Promise.resolve()
      )
      .then(() =>
        combo.languageDirty && combo.languageSaveFn ? combo.languageSaveFn() : Promise.resolve()
      )
      .then(() =>
        combo.keywordNegativeTermsDirty && combo.keywordNegativeTermsSaveFn
          ? combo.keywordNegativeTermsSaveFn()
          : Promise.resolve()
      )
      .then(() =>
        combo.locationDirty && combo.locationSaveFn ? combo.locationSaveFn() : Promise.resolve()
      );
  }, [combo]);

  const cancelDialogBag = useCancelDialog(dirty, combinedSave);

  // Navigation
  const history = useHistory();
  const match = useRouteMatch();
  const selectedId = (match.params as any).menuItem;
  const handleSelectMenuItem = (itemId: string) => history.push(`${path}/${itemId}`);
  const wrappedHandleSelectMenuItem = cancelDialogBag.wrapAction(handleSelectMenuItem);

  return (
    <>
      <ProjectAppBar project={project} cancelDialogBag={cancelDialogBag} user={user} />
      <UnsavedChangesDialog cancelDialogBag={cancelDialogBag} />
      <VoxelPageContainer className="px-24">
        <div className="flex flex-col gap-8 py-4">
          <div className="py-4">
            <VoxelText variant="title1">Project settings</VoxelText>
          </div>
          <div className="mt-12 grid grid-cols-12 gap-x-12">
            <div className="col-span-4 md:col-span-3 2xl:col-span-2">
              <VoxelMenu
                className="w-full"
                menuListItems={menuItems}
                selectedId={selectedId}
                setSelectedId={wrappedHandleSelectMenuItem}
              />
            </div>
            <div className="flex-grow col-span-8 md:col-span-9 2xl:col-span-10">
              <Switch>
                <Route
                  path={path + '/' + ProjectSettingsMenuChoices.General}
                  render={() => (
                    <GeneralSettingsPage
                      project={project}
                      reload={reload}
                      setNameDirty={(dirty) =>
                        setCombo((prev) => ({ ...prev, generalNameDirty: dirty }))
                      }
                      setNameSave={(saveFn) =>
                        setCombo((prev) => ({ ...prev, generalNameSaveFn: saveFn }))
                      }
                      setDomainDirty={(dirty) =>
                        setCombo((prev) => ({ ...prev, generalDomainDirty: dirty }))
                      }
                      setDomainSave={(saveFn) =>
                        setCombo((prev) => ({ ...prev, generalDomainSaveFn: saveFn }))
                      }
                      setCurrencyDirty={(dirty) =>
                        setCombo((prev) => ({ ...prev, generalCurrencyDirty: dirty }))
                      }
                      setCurrencySave={(saveFn) =>
                        setCombo((prev) => ({ ...prev, generalCurrencySaveFn: saveFn }))
                      }
                    />
                  )}
                />
                <Route
                  path={path + '/' + ProjectSettingsMenuChoices.DataCollectionSettings}
                  render={() => (
                    <DataSettings
                      project={project}
                      reload={reload}
                      setLanguageDirty={(dirty) =>
                        setCombo((prev) => ({ ...prev, languageDirty: dirty }))
                      }
                      setLanguageSave={(saveFn) =>
                        setCombo((prev) => ({ ...prev, languageSaveFn: saveFn }))
                      }
                      setNegativeTermsDirty={(dirty) =>
                        setCombo((prev) => ({ ...prev, keywordNegativeTermsDirty: dirty }))
                      }
                      setNegativeTermsSave={(saveFn) =>
                        setCombo((prev) => ({ ...prev, keywordNegativeTermsSaveFn: saveFn }))
                      }
                      setLocationDirty={(dirty) =>
                        setCombo((prev) => ({ ...prev, locationDirty: dirty }))
                      }
                      setLocationSave={(saveFn) =>
                        setCombo((prev) => ({ ...prev, locationSaveFn: saveFn }))
                      }
                    />
                  )}
                />
                <Route
                  path={path + '/' + ProjectSettingsMenuChoices.TeamAccess}
                  render={() => <TeamAccessPage project={project} reload={reload} user={user} />}
                />
              </Switch>
            </div>
          </div>
        </div>
      </VoxelPageContainer>
    </>
  );
};

export default ProjectSettingsPage;
